import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Sparkles, Loader2, RefreshCw, Trophy,
  Camera, FileText, ShieldCheck, Users,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter-168 Phase 3 — Admin Profile Quality Report.
 * Aggregate stats over all active talent. Drives the Top-Profile pipeline.
 */
const AdminProfileQuality = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/profile-quality/report');
      setReport(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const recomputeAll = async () => {
    setRefreshing(true);
    try {
      const res = await api.post('/admin/profile-quality/refresh-all');
      const jobId = res.data?.job_id;
      const queued = res.data?.queued || 0;
      toast.success(`Queued refresh for ${queued} talents — running in background…`);
      if (jobId) {
        // Poll status every 4s until complete (cap 5 min).
        let ticks = 0;
        const poll = async () => {
          try {
            const st = await api.get(`/admin/profile-quality/refresh-all/${jobId}`);
            if (st.data?.status === 'complete') {
              toast.success(`Done — refreshed ${st.data.refreshed}/${st.data.total}`);
              await load();
              setRefreshing(false);
              return;
            }
          } catch { /* ignore transient */ }
          ticks += 1;
          if (ticks < 75) {
            setTimeout(poll, 4000);
          } else {
            setRefreshing(false);
          }
        };
        setTimeout(poll, 4000);
      } else {
        await load();
        setRefreshing(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh failed');
      setRefreshing(false);
    }
  };

  const total = report?.total || 0;
  const pct = (n) => total ? Math.round((n / total) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-profile-quality-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/home')}
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-cyan-500" />
              Profile Quality Report
            </h1>
          </div>
          <Button
            onClick={recomputeAll}
            disabled={refreshing}
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
            data-testid="recompute-all-btn"
          >
            {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Recompute all
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>
        ) : !report ? (
          <Card><CardContent className="py-12 text-center text-gray-500">No data</CardContent></Card>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Card data-testid="stat-total">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-xs text-gray-500"><Users className="w-3.5 h-3.5" /> Active talent</div>
                  <p className="text-2xl font-bold mt-1">{total}</p>
                </CardContent>
              </Card>
              <Card data-testid="stat-top">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-xs text-gray-500"><Trophy className="w-3.5 h-3.5 text-emerald-500" /> Top Profiles</div>
                  <p className="text-2xl font-bold mt-1">{report.top_count} <span className="text-sm font-medium text-gray-500">({pct(report.top_count)}%)</span></p>
                </CardContent>
              </Card>
              <Card data-testid="stat-mid">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-xs text-gray-500"><Sparkles className="w-3.5 h-3.5 text-cyan-500" /> Mid (60-84)</div>
                  <p className="text-2xl font-bold mt-1">{report.mid_count} <span className="text-sm font-medium text-gray-500">({pct(report.mid_count)}%)</span></p>
                </CardContent>
              </Card>
              <Card data-testid="stat-low">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-xs text-gray-500"><Sparkles className="w-3.5 h-3.5 text-amber-500" /> Below 60</div>
                  <p className="text-2xl font-bold mt-1">{report.low_count} <span className="text-sm font-medium text-gray-500">({pct(report.low_count)}%)</span></p>
                </CardContent>
              </Card>
            </div>

            <Card className="mb-6">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm">Average score</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-cyan-600" data-testid="avg-score">{report.avg_score}</span>
                  <span className="text-lg text-gray-400">/ 100</span>
                </div>
              </CardContent>
            </Card>

            <h2 className="text-sm font-semibold text-gray-700 mb-2">Biggest gaps</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Card data-testid="gap-photo">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <Camera className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{report.missing_photo}</p>
                    <p className="text-xs text-gray-500">Missing profile photo</p>
                  </div>
                  <Badge variant="outline" className="ml-auto text-xs">{pct(report.missing_photo)}%</Badge>
                </CardContent>
              </Card>
              <Card data-testid="gap-bio">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{report.missing_bio}</p>
                    <p className="text-xs text-gray-500">Missing / short bio</p>
                  </div>
                  <Badge variant="outline" className="ml-auto text-xs">{pct(report.missing_bio)}%</Badge>
                </CardContent>
              </Card>
              <Card data-testid="gap-idv">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{report.missing_idv}</p>
                    <p className="text-xs text-gray-500">Not ID-verified</p>
                  </div>
                  <Badge variant="outline" className="ml-auto text-xs">{pct(report.missing_idv)}%</Badge>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminProfileQuality;
